import { FC } from "react";

import { Box, Card, HStack, Text, VStack } from "@/components/ui";

import { formatMargin, formatPercent, PARTY_COLOR, TOSSUP } from "./colors";
import InfoTip from "./InfoTip";
import type {
  ExternalTopline,
  Office,
  Party,
  SeatProjection,
  TrackerVariant,
} from "./types";

/* ── Types ─────────────────────────────────────── */

export interface GenericBallot {
  /** Share of the 2024 national vote covered by surveyed states. */
  coverage: number;
  margin: number;
}

interface OverviewProps {
  externalGeneric: number | null;
  foresightGeneric: GenericBallot | null;
  governors: Record<Party | "open", number> | null;
  house: SeatProjection | null;
  /** Contested House seats read from the surveyed states' average swing. */
  houseFromNational: number;
  layerLabel: string;
  office: Office;
  senate: SeatProjection | null;
  sourceLabel: string | null;
  topline: ExternalTopline | null;
  variant: TrackerVariant;
}

/* ── Constants ─────────────────────────────────── */

const HOUSE_HINT =
  "Each of the 435 districts, as drawn for 2026, starts from its 2024 presidential result and moves by the swing Foresight measures in its state, plus the district's own deviation once its counties have enough respondents. Odds come from 5,000 simulated elections with errors shared nationally (3 pts), within each state (its survey error) and per district. Seats whose finalists share a party, or with a single candidate, are decided. 218 seats for control.";

const SENATE_HINT =
  "Expected seats including the 65 senators not up this year (independents who caucus with Democrats are counted with them). Odds come from 20,000 simulated elections in which polling errors are shared nationally (3 pts) on top of each race's own uncertainty. Democrats need 51 seats; the Vice President breaks 50-50 ties for Republicans.";

/* ── Subcomponents ─────────────────────────────── */

interface Segment {
  color: string;
  key: string;
  value: number;
}

/** Stacked seat bar with a 2px surface gap between segments. */
const SeatStrip: FC<{ marker: number; segments: Segment[]; total: number }> = ({
  marker,
  segments,
  total,
}) => (
  <Box className="relative h-7 w-full">
    <HStack className="h-full w-full gap-0.5 rounded-md overflow-hidden">
      {segments
        .filter((s) => s.value > 0)
        .map((s) => (
          <Box
            key={s.key}
            style={{
              backgroundColor: s.color,
              flexBasis: 0,
              flexGrow: s.value,
            }}
          />
        ))}
    </HStack>
    <Box
      className="absolute -top-1 -bottom-1 w-0.5 bg-typography-900"
      style={{ left: `${(marker / total) * 100}%` }}
    />
  </Box>
);

/** Expected seats and control odds of a chamber. */
const SeatSummary: FC<{
  digits: number;
  hint: string;
  majority: string;
  marker: number;
  projection: SeatProjection;
  title: string;
  total: number;
}> = ({ digits, hint, majority, marker, projection, title, total }) => {
  const { expected, probabilities } = projection;
  return (
    <VStack className="gap-3">
      <HStack className="items-baseline justify-between flex-wrap gap-2">
        <HStack className="items-center gap-1">
          <Text className="text-sm font-semibold text-typography-900">
            {title}
          </Text>
          <InfoTip text={hint} />
        </HStack>
        <Text className="text-xs text-typography-500">{majority}</Text>
      </HStack>
      <SeatStrip
        marker={marker}
        segments={[
          { color: PARTY_COLOR.D, key: "D", value: expected.D },
          { color: PARTY_COLOR.I, key: "I", value: expected.I },
          { color: PARTY_COLOR.R, key: "R", value: expected.R },
        ]}
        total={total}
      />
      <HStack className="justify-between flex-wrap gap-2">
        <Text className="text-sm font-semibold text-typography-900">
          D {expected.D.toFixed(digits)}
          {expected.I >= 0.5 * 10 ** -digits
            ? ` · I ${expected.I.toFixed(digits)}`
            : ""}
        </Text>
        <Text className="text-sm text-typography-700">
          Control: D {formatPercent(probabilities.demControl)} · R{" "}
          {formatPercent(probabilities.repControl)}
          {probabilities.independentsDecide >= 0.005
            ? ` · independents decide ${formatPercent(probabilities.independentsDecide)}`
            : ""}
        </Text>
        <Text className="text-sm font-semibold text-typography-900">
          R {expected.R.toFixed(digits)}
        </Text>
      </HStack>
    </VStack>
  );
};

const Note: FC<{ text: string }> = ({ text }) => (
  <Text className="text-xs text-typography-400">{text}</Text>
);

/* ── Component ─────────────────────────────────── */

const Overview: FC<OverviewProps> = ({
  externalGeneric,
  foresightGeneric,
  governors,
  house,
  houseFromNational,
  layerLabel,
  office,
  senate,
  sourceLabel,
  topline,
  variant,
}) => {
  const ownModel = topline && sourceLabel ? `${sourceLabel}’s own model` : null;

  if (office === "house") {
    return (
      <Card className="p-5 rounded-2xl border border-outline-200">
        <VStack className="gap-3">
          {house ? (
            <SeatSummary
              digits={0}
              hint={HOUSE_HINT}
              majority="435 seats · 218 for control"
              marker={217.5}
              projection={house}
              title={`House after Nov 3 · ${layerLabel}`}
              total={435}
            />
          ) : variant === "admin" ? (
            <Text className="text-sm text-typography-500">
              No district-level read in this layer yet: field a wave, or compare
              with a source that has House districts (Silver Bulletin is built
              in).
            </Text>
          ) : (
            <Text className="text-sm text-typography-500">
              No House read yet.
            </Text>
          )}
          {house && houseFromNational > 0 && (
            <Note
              text={`${houseFromNational} contested seats are in states not surveyed yet: they use the surveyed states’ average swing, with wider error.`}
            />
          )}
          {house && house.priors > 0 && (
            <Note
              text={`${house.priors} seats without data in this layer keep their current party as a placeholder.`}
            />
          )}
          {ownModel && topline && (
            <Note
              text={`${ownModel}: D control ${formatPercent(topline.house.demControl)} · ${topline.house.demSeats.toFixed(0)} D seats on average.`}
            />
          )}
          <HStack className="flex-wrap items-center gap-x-10 gap-y-3 border-t border-outline-200 pt-3">
            <VStack className="gap-0.5">
              <HStack className="items-center gap-1">
                <Text className="text-xs font-medium uppercase tracking-wide text-typography-500">
                  Generic ballot · Foresight
                </Text>
                <InfoTip text="Each state's 'Democrat or Republican for Congress' read, weighted by its 2024 turnout." />
              </HStack>
              <Text className="text-2xl font-bold text-typography-900">
                {foresightGeneric ? formatMargin(foresightGeneric.margin) : "—"}
              </Text>
              <Text className="text-xs text-typography-400">
                {foresightGeneric
                  ? `States surveyed cover ${formatPercent(foresightGeneric.coverage)} of the 2024 vote`
                  : "No state surveyed yet"}
              </Text>
            </VStack>
            {sourceLabel && (
              <VStack className="gap-0.5">
                <Text className="text-xs font-medium uppercase tracking-wide text-typography-500">
                  Generic ballot · {sourceLabel}
                </Text>
                <Text className="text-2xl font-bold text-typography-900">
                  {formatMargin(externalGeneric)}
                </Text>
                <Text className="text-xs text-typography-400">
                  {externalGeneric === null
                    ? "Paste a “National” or “Generic ballot” line to set it"
                    : "National House vote from this source"}
                </Text>
              </VStack>
            )}
          </HStack>
        </VStack>
      </Card>
    );
  }

  if (office === "governor" && governors) {
    return (
      <Card className="p-5 rounded-2xl border border-outline-200">
        <VStack className="gap-3">
          <HStack className="items-baseline justify-between flex-wrap gap-2">
            <Text className="text-sm font-semibold text-typography-900">
              Governors after Nov 3 · {layerLabel}
            </Text>
            <Text className="text-xs text-typography-500">
              36 races · 14 governors not up
            </Text>
          </HStack>
          <SeatStrip
            marker={25}
            segments={[
              { color: PARTY_COLOR.D, key: "D", value: governors.D },
              { color: PARTY_COLOR.I, key: "I", value: governors.I },
              { color: TOSSUP, key: "open", value: governors.open },
              { color: PARTY_COLOR.R, key: "R", value: governors.R },
            ]}
            total={50}
          />
          <HStack className="justify-between">
            <Text className="text-sm font-semibold text-typography-900">
              D {governors.D}
            </Text>
            <Text className="text-xs text-typography-500">
              {governors.open} toss-up or not yet read
              {governors.I > 0 ? ` · I ${governors.I}` : ""}
            </Text>
            <Text className="text-sm font-semibold text-typography-900">
              R {governors.R}
            </Text>
          </HStack>
          {ownModel && topline && (
            <Note
              text={`${ownModel}: Democrats hold most governorships ${formatPercent(topline.governors.demMajority)} · Republicans ${formatPercent(topline.governors.repMajority)} · 25–25 tie ${formatPercent(topline.governors.tie)}.`}
            />
          )}
        </VStack>
      </Card>
    );
  }

  if (!senate) return null;

  return (
    <Card className="p-5 rounded-2xl border border-outline-200">
      <VStack className="gap-3">
        <SeatSummary
          digits={1}
          hint={SENATE_HINT}
          majority="35 seats up · 51 for Democratic control"
          marker={50}
          projection={senate}
          title={`Senate after Nov 3 · ${layerLabel}`}
          total={100}
        />
        {senate.priors > 0 && (
          <Note
            text={
              variant === "admin"
                ? `${senate.priors} race${senate.priors === 1 ? "" : "s"} without data in this layer use the Cook rating as a placeholder.`
                : `Races not surveyed yet (${senate.priors}) use a placeholder rating.`
            }
          />
        )}
        {ownModel && topline && (
          <Note
            text={`${ownModel}: D control ${formatPercent(topline.senate.demControl)} · ${topline.senate.demSeats.toFixed(1)} D seats on average.`}
          />
        )}
      </VStack>
    </Card>
  );
};

export default Overview;
